import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Settings as SettingsIcon, User, Shield, Bell, Save, Files, ShoppingBag, Lock, Sparkles, Check, Loader2, CheckCircle2, Receipt, Utensils, AlertTriangle, Calendar, Banknote, Trash2, Edit2, Download, UserX, RotateCcw } from "lucide-react";
import { useTaxYear } from "../contexts/TaxYearContext";
import AutoTopOffCard from "../components/AutoTopOffCard";
import YearEndManagement from "../components/YearEndManagement";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmtN = (n) => new Intl.NumberFormat("en-US").format(n || 0);

const PAGE_PACKS = [
  { sku: "pack_50", pages: 50, price: 14.99, label: "50 Pages", testId: "pack-50" },
  { sku: "pack_250", pages: 250, price: 49.99, label: "250 Pages", popular: true, testId: "pack-250" },
  { sku: "pack_1000", pages: 1000, price: 129, label: "1,000 Pages", testId: "pack-1000" },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { taxYear } = useTaxYear();
  const isSponsored = user?.account_type === "sponsored" || user?.license?.type === "sponsored";
  // Phase B — add-on packs are bought FOR a specific tax year, and only for a
  // year backed by a paid plan or an owner grant.
  const [packYears, setPackYears] = useState([]);
  const [packYear, setPackYear] = useState(null);
  const [tab, setTab] = useState("usage");
  const [msg, setMsg] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [usage, setUsage] = useState(null);
  const [busySku, setBusySku] = useState(null);
  const [history, setHistory] = useState([]);
  const [successReceipt, setSuccessReceipt] = useState(null);
  // Sprint 2 Batch 4 — Account tab state
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletionBusy, setDeletionBusy] = useState(false);

  // Detect ?purchase=success redirect from Square hosted checkout.
  // Stage C — also handle ?tab=usage&focus=buy (Quota bell deep-link).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "usage" || tabParam === "profile" || tabParam === "license" || tabParam === "cacfp" || tabParam === "year-end" || tabParam === "account") {
      setTab(tabParam);
    }
    if (params.get("focus") === "buy") {
      // Scroll to the Buy More Pages card once it renders.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="buy-pages-card"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
    if (params.get("purchase") === "success") {
      const skuFromUrl = params.get("sku") || "";
      const pack = PAGE_PACKS.find(p => p.sku === skuFromUrl);
      setSuccessReceipt({
        sku: skuFromUrl,
        label: pack?.label || "Page Pack",
        pages: pack?.pages || 0,
        price: pack?.price || 0,
      });
      // Re-fetch usage shortly to capture the webhook-credited pages.
      const refreshAll = () => {
        axios.get(`${API}/usage`, { params: { tax_year: taxYear }, withCredentials: true }).then(r => setUsage(r.data)).catch(() => {});
        axios.get(`${API}/usage/purchase-history`, { withCredentials: true }).then(r => setHistory(r.data.purchases || [])).catch(() => {});
      };
      const t1 = setTimeout(refreshAll, 2500);
      const t2 = setTimeout(refreshAll, 6000);
      const t3 = setTimeout(refreshAll, 12000);
      // Clear the URL so a refresh doesn't replay the message.
      window.history.replaceState({}, document.title, window.location.pathname);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/usage`, { params: { tax_year: taxYear }, withCredentials: true }).then(res => setUsage(res.data)).catch(() => {});
    axios.get(`${API}/usage/purchase-history`, { withCredentials: true }).then(res => setHistory(res.data.purchases || [])).catch(() => {});
  }, [taxYear]);

  // Phase B — which years can receive add-on pages (paid plan / owner grant).
  useEffect(() => {
    if (isSponsored) return;
    axios.get(`${API}/payments/page-pack/eligible-years`, { withCredentials: true })
      .then(res => {
        const years = res.data?.years || [];
        setPackYears(years);
        setPackYear(prev => (prev && years.includes(prev) ? prev : (years.includes(taxYear) ? taxYear : (years[0] ?? null))));
      })
      .catch(() => {});
  }, [taxYear, isSponsored]);

  const license = user?.license || {};
  // Derive expiry date from the license payload. license_expires_at is set by the
  // Square webhook on every paid/renewal event; trial_end is set on registration.
  // Owner/signature accounts never see this label (they're filtered above).
  const licenseExpiry = license.type === "paid" && license.license_expires_at
    ? new Date(license.license_expires_at).toLocaleDateString()
    : license.trial_end
      ? new Date(license.trial_end).toLocaleDateString()
      : "N/A";

  const handleBuyPack = async (pack) => {
    setPurchaseError("");
    if (!packYear) {
      setPurchaseError("Activate a tax year first — add-on pages are purchased for a specific tax year.");
      return;
    }
    setBusySku(pack.sku);
    try {
      const { data } = await axios.post(
        `${API}/payments/page-pack/checkout`,
        { sku: pack.sku, tax_year: packYear },
        { withCredentials: true }
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setPurchaseError("Could not start checkout — please try again.");
        setBusySku(null);
      }
    } catch (err) {
      setPurchaseError(err.response?.data?.detail || "Checkout failed. Please try again.");
      setBusySku(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your account and preferences</p>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {[
          { id: "usage", icon: Files, label: "Usage" },
          { id: "profile", icon: User, label: "Profile" },
          { id: "license", icon: Shield, label: "Tax Year Access" },
          { id: "cacfp", icon: Utensils, label: "CACFP" },
          { id: "bank-formats", icon: Banknote, label: "Bank Formats" },
          { id: "year-end", icon: Lock, label: "Year-End" },
          { id: "account", icon: UserX, label: "Account" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {successReceipt && (
        <div
          data-testid="purchase-success-receipt"
          className="bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-300 rounded-xl shadow-sm p-6 flex items-start gap-5 animate-in fade-in slide-in-from-top-4 duration-500"
        >
          <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-emerald-900 text-lg" style={{ fontFamily: "Manrope" }}>Payment Successful — Thank you!</h2>
            <p className="text-emerald-700 text-sm mt-1">
              <span className="font-semibold">+{successReceipt.pages} pages</span> have been added to your account
              {successReceipt.price > 0 && <> for <span className="font-semibold">${successReceipt.price.toFixed(2)}</span></>}.
              {" "}A receipt was emailed by Square. Pages will appear in your meter within a few seconds.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setSuccessReceipt(null)}
                data-testid="dismiss-receipt"
                className="text-xs font-semibold text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-50 rounded-lg px-3 py-1.5 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm" data-testid="settings-msg">
          {msg}
        </div>
      )}

      {purchaseError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start justify-between gap-3" data-testid="purchase-error">
          <div>
            <p className="font-semibold">Checkout could not start</p>
            <p className="text-xs mt-0.5">{purchaseError}</p>
          </div>
          <button
            onClick={() => setPurchaseError("")}
            className="text-red-700 text-xs underline hover:text-red-900 whitespace-nowrap"
          >
            Dismiss
          </button>
        </div>
      )}

      {tab === "usage" && (
        <div className="space-y-6">
          {/* Usage Meter */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="usage-meter-card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Files className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>My Document Processing Usage</h2>
                {!usage ? (
                  <div className="h-20 bg-slate-100 rounded-lg animate-pulse mt-3" />
                ) : usage.status === "unlimited" ? (
                  <div className="mt-3 bg-purple-50 border border-purple-100 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-semibold flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-600" /> Unlimited pages — Owner / Signature account
                    </p>
                    <p className="text-xs text-purple-600 mt-1">Page tracking does not apply to your account.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }} data-testid="usage-pages-used">
                      {fmtN(usage.pages_used_year)} <span className="text-slate-400 font-normal text-xl">/ {fmtN(usage.pages_quota_year)}</span>
                      <span className="text-base text-slate-500 font-normal"> pages used</span>
                    </p>
                    <div className="mt-3 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        data-testid="usage-progress-bar"
                        className={`h-full rounded-full transition-all ${
                          usage.status === "maxed_out" ? "bg-red-500" :
                          usage.status === "critical" ? "bg-orange-500" :
                          usage.status === "warning" ? "bg-amber-400" :
                          "bg-emerald-500"
                        }`}
                        style={{ width: `${Math.min(usage.percent_used, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-slate-600 font-semibold" data-testid="usage-pages-remaining">{fmtN(usage.remaining)} pages remaining</span>
                      {usage.quota_reset_date ? (
                        <span className="text-slate-400">Resets on {new Date(usage.quota_reset_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      ) : (
                        <span className="text-slate-400" data-testid="usage-year-scope">Tax Year {taxYear} balance</span>
                      )}
                    </div>
                    {usage.pages_purchased_extra > 0 && (
                      <p className="text-xs text-emerald-600 font-semibold mt-2">
                        + {fmtN(usage.pages_purchased_extra)} bonus pages from add-on packs (never expire)
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Buy More Pages — hidden for sponsored accounts */}
          {isSponsored ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-3" data-testid="sponsored-pages-notice">
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div>
                <p className="text-sm font-semibold text-blue-900" style={{ fontFamily: "Manrope" }}>Need more pages?</p>
                <p className="text-sm text-blue-700 mt-0.5">Contact your account sponsor to have additional pages added to your account.</p>
              </div>
            </div>
          ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="buy-pages-card">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag className="w-5 h-5 text-emerald-600" />
              <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Buy More Pages</h2>
            </div>
            <p className="text-sm text-slate-500 mb-5">Add-on packs never expire — they stay on the tax year you buy them for.</p>
            {packYears.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5" data-testid="pack-no-eligible-years">
                <p className="text-sm text-amber-800">
                  Add-on pages apply to a specific tax year. Activate a tax year from the Pricing page first.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-5" data-testid="pack-year-selector-row">
                <label className="text-sm font-semibold text-slate-700" htmlFor="pack-year-select">Apply pages to</label>
                <select
                  id="pack-year-select"
                  data-testid="pack-year-select"
                  value={packYear ?? ""}
                  onChange={(e) => setPackYear(Number(e.target.value))}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-900 bg-white"
                >
                  {packYears.map(y => (
                    <option key={y} value={y}>Tax Year {y}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PAGE_PACKS.map(pack => (
                <div
                  key={pack.sku}
                  data-testid={pack.testId}
                  className={`relative border rounded-xl p-5 ${pack.popular ? "border-emerald-500 bg-emerald-50/30" : "border-slate-200 bg-white"}`}
                >
                  {pack.popular && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                      Best Value
                    </span>
                  )}
                  <p className="font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>{pack.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>${pack.price}</p>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />One-time purchase</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />Never expires</p>
                  <button
                    onClick={() => handleBuyPack(pack)}
                    data-testid={`buy-${pack.sku}`}
                    disabled={busySku !== null || !packYear}
                    className={`w-full mt-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${
                      pack.popular ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    }`}
                  >
                    {busySku === pack.sku ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>Buy {pack.label}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-4 flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" /> Secure payment powered by Square
            </p>
          </div>
          )} {/* end !isSponsored Buy More Pages */}

          {/* Purchase History — hidden for sponsored */}
          {!isSponsored && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="purchase-history-card">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-5 h-5 text-slate-500" />
              <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Purchase History</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Your add-on page-pack purchases. Receipts emailed by Square at time of purchase.</p>
            {history.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-slate-200 rounded-lg" data-testid="history-empty-state">
                <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No purchases yet</p>
                <p className="text-xs text-slate-400 mt-0.5">Page packs you buy will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="history-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pack</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pages</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const skuLabel = { pack_50: "50 Pages", pack_250: "250 Pages", pack_1000: "1,000 Pages" }[h.sku] || h.sku;
                      return (
                        <tr key={h.square_payment_id || i} className="border-b border-slate-50">
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            {h.created_at ? new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{skuLabel}</td>
                          <td className="px-4 py-3 text-right text-emerald-700 font-semibold">+{fmtN(h.pages)}</td>
                          <td className="px-4 py-3 text-right text-slate-900 font-semibold">${(h.price_usd || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-xs text-slate-400">
                            {h.square_payment_id ? <span className="font-mono">…{String(h.square_payment_id).slice(-6)}</span> : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )} {/* end !isSponsored Purchase History */}

          {/* Auto Top-Off — hidden for sponsored accounts */}
          {!isSponsored && <AutoTopOffCard />}
        </div>
      )}

      {tab === "profile" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Account Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Name</label>
              <input type="text" defaultValue={user?.name || ""} disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
              <input type="email" defaultValue={user?.email || ""} disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Center Name</label>
              <input type="text" defaultValue={user?.center_name || ""} disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Account Role</label>
              <input type="text" defaultValue={user?.role || "user"} disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
            </div>
          </div>
          <p className="text-slate-400 text-xs">To update your profile information, please contact support.</p>
        </div>
      )}

      {tab === "license" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="license-status-card">
            <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Tax Year Access</h2>
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${license.status === "active" || license.status === "paid" ? "bg-emerald-100" : license.status === "trial" ? "bg-blue-100" : "bg-red-100"}`}>
                <Shield className={`w-7 h-7 ${license.status === "active" || license.status === "paid" ? "text-emerald-600" : license.status === "trial" ? "text-blue-600" : "text-red-600"}`} />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg" style={{ fontFamily: "Manrope" }}>
                  {license.type === "signature" ? "Signature Account — Lifetime Tax Year Access" :
                    license.status === "trial" ? `Free Trial — ${license.days_remaining} days remaining` :
                    license.status === "active" || license.type === "paid" ? "Tax Year Access — Active" : "Tax Year Access — Expired"}
                </p>
                <p className="text-slate-500 text-sm">
                  {license.type === "signature" ? "Full access to every tax year, no expiry, no renewal needed" :
                    license.status === "trial" ? "Trial expires " + new Date(license.trial_end).toLocaleDateString() : "Valid through " + licenseExpiry}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-xl p-4">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Plan</p>
                <p className="text-slate-900 font-semibold mt-1">
                  {license.type === "signature" ? "Signature" :
                    license.type === "paid" ? "Annual Tax Year Access" : "Free Trial"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Price</p>
                <p className="text-slate-900 font-semibold mt-1">
                  {license.type === "signature" ? "Lifetime (included)" : "From $99/tax year"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Valid Through</p>
                <p className="text-slate-900 font-semibold mt-1">
                  {license.type === "signature" ? "No expiration" :
                    license.type === "paid" ? "1 year from activation" : "End of 14-day trial"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Status</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                  license.type === "signature" ? "bg-emerald-50 text-emerald-700" :
                  license.status === "trial" ? "bg-blue-50 text-blue-700" :
                  license.type === "paid" ? "bg-emerald-50 text-emerald-700" :
                  "bg-red-50 text-red-700"
                }`}>
                  {license.type === "signature" ? "Active" :
                    license.status === "trial" ? "Trial" :
                    license.type === "paid" ? "Active" : "Expired"}
                </span>
              </div>
            </div>
          </div>

          {license.type !== "paid" && license.type !== "signature" && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 text-sm mb-3" style={{ fontFamily: "Manrope" }}>Activate Tax Year Access</h3>
              <p className="text-slate-500 text-sm mb-4">Pick a plan to unlock a full tax year of access — Basic, Premium, or Premium Plus.</p>
              <div className="flex gap-3">
                <button onClick={() => window.location.href = "/app/pricing"}
                  data-testid="settings-upgrade-btn"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-all">
                  View Plans →
                </button>
              </div>
            </div>
          )}

          {/* Signature owner — slim "View Plans" card so the owner can review
              public pricing without needing to log out. No upgrade CTA / no
              demo button — signature already has lifetime access. */}
          {license.type === "signature" && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="signature-view-plans-card">
              <h3 className="font-bold text-slate-900 text-sm mb-3" style={{ fontFamily: "Manrope" }}>Review Pricing</h3>
              <p className="text-slate-500 text-sm mb-4">
                Your signature account has lifetime access — no payment needed. Open the public pricing page to review what Basic, Premium, and Premium Plus customers see (handy when explaining pricing to a sponsored center).
              </p>
              <div className="flex gap-3">
                <button onClick={() => window.location.href = "/app/pricing"}
                  data-testid="signature-view-plans-btn"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-all">
                  View Plans →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "cacfp" && <CacfpSettingsPanel />}
      {tab === "bank-formats" && <BankFormatsPanel />}
      {tab === "year-end" && <YearEndManagement />}
      {tab === "account" && (
        <AccountTab
          user={user}
          refreshUser={refreshUser}
          exportLoading={exportLoading}
          setExportLoading={setExportLoading}
          deleteConfirm={deleteConfirm}
          setDeleteConfirm={setDeleteConfirm}
          deleteConfirmText={deleteConfirmText}
          setDeleteConfirmText={setDeleteConfirmText}
          deletionBusy={deletionBusy}
          setDeletionBusy={setDeletionBusy}
        />
      )}
    </div>
  );
}

// ── CACFP Program Settings panel (Ship Gate 0a) ─────────────────────────────
function CacfpSettingsPanel() {
  const { user, refreshUser } = useAuth();
  const [participation, setParticipation] = useState(user?.cacfp_participation || (user?.cacfp_participant ? "active" : "none"));
  const [fyMode, setFyMode] = useState(user?.cacfp_fy_mode || "calendar");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null);  // null | "fyChange" | "turnOff"
  const [hasCacfpData, setHasCacfpData] = useState(false);

  useEffect(() => {
    // Lightweight check: if user has any CACFP ledger entries, flipping FY mode is
    // a destructive operation (re-buckets historical data). We warn before save.
    axios.get(`${API}/transactions/summary`, { withCredentials: true })
      .then(({ data }) => {
        const sb = data?.synthetic_breakdown || {};
        setHasCacfpData(Object.values(sb).some(v => v > 0));
      })
      .catch(() => {});
  }, []);

  const currentMode = user?.cacfp_fy_mode || "calendar";
  const currentPart = user?.cacfp_participation || (user?.cacfp_participant ? "active" : "none");
  const hasChanged = participation !== currentPart || fyMode !== currentMode;
  const flippingFyWithData = currentPart === "active" && participation === "active" && fyMode !== currentMode && hasCacfpData;
  const turningOff = currentPart === "active" && participation !== "active";

  const persist = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/cacfp`, {
        cacfp_participation: participation,
        cacfp_fy_mode: participation === "active" ? fyMode : null,
      }, { withCredentials: true });
      await refreshUser();
      setConfirmOpen(null);
      toast.success("CACFP settings updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (flippingFyWithData) { setConfirmOpen("fyChange"); return; }
    if (turningOff) { setConfirmOpen("turnOff"); return; }
    persist();
  };

  return (
    <div className="space-y-6" data-testid="cacfp-settings-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Utensils className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>CACFP Program</h3>
            <p className="text-xs text-slate-500">USDA Child & Adult Care Food Program — affects allocation tools and Tax Prep merges</p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <p className="text-sm font-bold text-slate-800">Participation status</p>
          {[
            { id: "active",  label: "Active",                desc: "Currently enrolled — calculators editable, totals merge into Tax Prep" },
            { id: "pending", label: "Application pending",   desc: "Applied but not yet approved — calculators read-only, no merge yet" },
            { id: "none",    label: "Not a participant",     desc: "CACFP tools hidden / read-only, no merge" },
          ].map(({ id, label, desc }) => (
            <button
              key={id}
              data-testid={`cacfp-settings-participation-${id}`}
              onClick={() => setParticipation(id)}
              className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                participation === id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
              </div>
              {participation === id && <Check className="w-4 h-4 text-emerald-600 ml-auto flex-shrink-0" />}
            </button>
          ))}
        </div>

        {participation === "active" && (
          <div className="mt-6 space-y-3 border-t border-slate-100 pt-5">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-500" /> CACFP fiscal year</p>
            {[
              { id: "calendar", label: "Calendar Year (Jan 1 – Dec 31)",    desc: "Matches IRS Schedule C — most common for small operators", badge: "Recommended" },
              { id: "usda_fy",  label: "USDA Federal FY (Oct 1 – Sept 30)", desc: "USDA reporting standard" },
            ].map(({ id, label, desc, badge }) => (
              <button
                key={id}
                data-testid={`cacfp-settings-fy-${id}`}
                onClick={() => setFyMode(id)}
                className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                  fyMode === id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                    {badge && <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">{badge}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                </div>
                {fyMode === id && <Check className="w-4 h-4 text-emerald-600 ml-auto flex-shrink-0" />}
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={!hasChanged || saving}
            data-testid="cacfp-settings-save"
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold px-6 py-2 rounded-lg transition-all flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>

      {/* Confirmation modals */}
      {confirmOpen === "fyChange" && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="confirm-fy-change">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Change CACFP fiscal year mode?</h4>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  You have CACFP data already entered. Switching to <strong>{fyMode === "calendar" ? "Calendar Year" : "USDA FY"}</strong> will re-group your existing entries into the new year buckets. Your data is preserved, but reports will show different totals per year.
                </p>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  This is allowed under FNS 796-2 Rev. 4 as long as it's used consistently going forward and documented.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmOpen(null)} className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2" data-testid="confirm-fy-cancel">Cancel</button>
              <button onClick={persist} disabled={saving} className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="confirm-fy-proceed">
                {saving ? "Saving…" : "Yes, change & re-group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen === "turnOff" && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="confirm-turn-off">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Turn off CACFP?</h4>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  Your CACFP allocation, Use Charge, Monthly Ledger, and Food &amp; Labor data will be <strong>preserved</strong>, but they'll be hidden from your Dashboard and Tax Prep until you re-enable CACFP. Re-enabling restores everything in place.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmOpen(null)} className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2" data-testid="confirm-off-cancel">Cancel</button>
              <button onClick={persist} disabled={saving} className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="confirm-off-proceed">
                {saving ? "Saving…" : "Yes, turn off CACFP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Bank Formats panel — Settings → Bank Formats ────────────────────────────
// Lists every saved spreadsheet column-mapping the user has accumulated
// (each one was created by clicking ☑ "Save this mapping" on a spreadsheet
// import). Users can rename or delete them; deleting forces the next upload
// of that format to re-prompt the mapping panel.
function BankFormatsPanel() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingFp, setEditingFp] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/spreadsheet-mappings`, { withCredentials: true });
      setMappings(data.mappings || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load bank formats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (m) => { setEditingFp(m.fingerprint); setDraftLabel(m.label || ""); };
  const cancelEdit = () => { setEditingFp(null); setDraftLabel(""); };
  const saveEdit = async (fp) => {
    try {
      await axios.patch(`${API}/spreadsheet-mappings/${fp}`, { label: draftLabel.trim() }, { withCredentials: true });
      cancelEdit();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not rename.");
    }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.label || "this format"}"? Next upload from this bank will re-prompt the column mapping.`)) return;
    try {
      await axios.delete(`${API}/spreadsheet-mappings/${m.fingerprint}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not delete.");
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-5" data-testid="bank-formats-panel">
      <div>
        <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Bank Formats</h2>
        <p className="text-slate-500 text-sm mt-1">
          Saved column mappings from your spreadsheet imports. When you upload a file with the same column headers as one of these, the mapping is applied automatically.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="bank-formats-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading saved formats…
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="bank-formats-error">{error}</div>
      )}

      {!loading && !error && mappings.length === 0 && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-center" data-testid="bank-formats-empty">
          <Banknote className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-600 text-sm font-medium">No bank formats saved yet</p>
          <p className="text-slate-500 text-xs mt-1">When you import a spreadsheet (.csv, .xlsx, .xls), check "Save this mapping" to remember the column layout for future uploads from the same bank.</p>
        </div>
      )}

      {!loading && !error && mappings.length > 0 && (
        <div className="overflow-x-auto" data-testid="bank-formats-table">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-slate-700">Bank Format</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-700">Sheet</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-700">Mapped Columns</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-700">Last Updated</th>
                <th className="px-4 py-2 text-right font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => {
                const cols = [
                  m.mapping?.date_col && `Date: ${m.mapping.date_col}`,
                  m.mapping?.description_col && `Desc: ${m.mapping.description_col}`,
                  m.mapping?.amount_col && `Amount: ${m.mapping.amount_col}`,
                  (m.mapping?.debit_col && m.mapping?.credit_col) && `Debit/Credit: ${m.mapping.debit_col} / ${m.mapping.credit_col}`,
                ].filter(Boolean).join(" • ");
                const isEditing = editingFp === m.fingerprint;
                return (
                  <tr key={m.fingerprint} className="border-t border-slate-100" data-testid={`bank-format-row-${m.fingerprint}`}>
                    <td className="px-4 py-3 text-slate-900 font-medium">
                      {isEditing ? (
                        <input
                          value={draftLabel}
                          onChange={(e) => setDraftLabel(e.target.value)}
                          autoFocus
                          data-testid={`bank-format-label-input-${m.fingerprint}`}
                          className="border border-slate-300 rounded-md px-2 py-1 text-sm w-56 focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                        />
                      ) : (m.label || "Unnamed format")}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{m.sheet_name || "Sheet1"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{cols || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmt(m.updated_at || m.created_at)}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(m.fingerprint)} data-testid={`bank-format-save-${m.fingerprint}`} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">Save</button>
                          <button onClick={cancelEdit} className="text-slate-500 hover:text-slate-700 text-xs">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(m)} data-testid={`bank-format-edit-${m.fingerprint}`} className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 text-xs font-semibold">
                            <Edit2 className="w-3.5 h-3.5" /> Rename
                          </button>
                          <button onClick={() => remove(m)} data-testid={`bank-format-delete-${m.fingerprint}`} className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs font-semibold">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Account Tab Component (Sprint 2 Batch 4) ─────────────────────────────────
function AccountTab({ user, refreshUser, exportLoading, setExportLoading, deleteConfirm, setDeleteConfirm, deleteConfirmText, setDeleteConfirmText, deletionBusy, setDeletionBusy }) {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const isPendingDeletion = user?.deletion_status === "pending_deletion";
  const isOwner = user?.role === "owner" || user?.account_type === "signature";

  const daysLeft = (() => {
    if (!user?.deletion_permanent_after) return null;
    const ms = new Date(user.deletion_permanent_after) - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  })();

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const resp = await axios.get(`${API}/user/export-data`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (resp.headers["content-disposition"] || "").split('filename="')[1]?.replace('"', '') || "sst_export.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeletionBusy(true);
    try {
      await axios.post(`${API}/user/request-deletion`, {}, { withCredentials: true });
      await refreshUser();
      setDeleteConfirm(false);
      setDeleteConfirmText("");
      toast.success("Account scheduled for deletion. You have 7 days to restore it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed.");
    } finally {
      setDeletionBusy(false);
    }
  };

  const handleRestore = async () => {
    setDeletionBusy(true);
    try {
      await axios.post(`${API}/user/restore-account`, {}, { withCredentials: true });
      await refreshUser();
      toast.success("Account restored successfully.");
    } catch {
      toast.error("Restore failed. Please try again.");
    } finally {
      setDeletionBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export My Data */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Download className="w-4 h-4 text-blue-600" />
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Export My Data</h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-600 mb-1">Download a complete archive of all your data — transactions, notes, equipment, CACFP records, and archived PDFs.</p>
          <p className="text-xs text-slate-400 mb-5">Delivered as a ZIP file. All financial data included in CSV format.</p>
          <button
            data-testid="export-data-btn"
            onClick={handleExport}
            disabled={exportLoading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #2563EB, #6D28D9)" }}
          >
            {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exportLoading ? "Preparing…" : "Download My Data"}
          </button>
        </div>
      </div>

      {/* Delete / Restore Account — owner accounts cannot self-delete */}
      {!isOwner && (
        <div className="bg-white border border-red-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-red-100 flex items-center gap-2">
            <UserX className="w-4 h-4 text-red-600" />
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>
              {isPendingDeletion ? "Account Pending Deletion" : "Delete My Account"}
            </h2>
          </div>
          <div className="p-6">
            {isPendingDeletion ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Your account is scheduled for permanent deletion</p>
                    <p className="text-sm text-red-700 mt-1">
                      {daysLeft !== null
                        ? daysLeft > 0
                          ? `All your data will be permanently deleted in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}. This cannot be undone.`
                          : "Your account is being permanently deleted now."
                        : "All your data will be permanently deleted soon."}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="restore-account-btn"
                  onClick={handleRestore}
                  disabled={deletionBusy}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60 bg-emerald-600 hover:bg-emerald-700"
                >
                  {deletionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  {deletionBusy ? "Restoring…" : `Restore My Account${daysLeft !== null ? ` (${daysLeft}d left)` : ""}`}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">This will schedule permanent deletion of your account and all associated data — transactions, documents, CACFP records, reports — after a 7-day grace period.</p>
                <p className="text-sm text-slate-600">You can restore your account anytime during those 7 days by returning to this page.</p>
                {!deleteConfirm ? (
                  <button
                    data-testid="delete-account-btn"
                    onClick={() => setDeleteConfirm(true)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-red-600 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors"
                  >
                    <UserX className="w-4 h-4" />
                    Delete My Account
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-red-800">Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded">DELETE</span> to confirm</p>
                    <input
                      data-testid="delete-confirm-input"
                      type="text"
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE here"
                      className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                    <div className="flex gap-3">
                      <button
                        data-testid="delete-account-confirm-btn"
                        onClick={handleRequestDeletion}
                        disabled={deleteConfirmText !== "DELETE" || deletionBusy}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-red-700 transition-colors"
                      >
                        {deletionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                        {deletionBusy ? "Processing…" : "Schedule Deletion"}
                      </button>
                      <button
                        onClick={() => { setDeleteConfirm(false); setDeleteConfirmText(""); }}
                        className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
